import { QueryClient } from "@tanstack/react-query";

/**
 * Platform-wide React Query defaults.
 *
 * Design intent (per product call 2026-05-23): for an internal tool, data
 * freshness is on-demand, not automatic. Pages should load instantly from
 * cached data (even if old); users see "as of {timestamp}" + can hit a
 * refresh button when they want current data.
 *
 * Practically:
 *   - refetchOnMount: false → page navigations don't auto-refetch
 *   - refetchOnWindowFocus: false → switching tabs doesn't auto-refetch
 *   - staleTime: 5min → cached data is fresh enough for casual reuse
 *   - gcTime: 30min → keep results around for back-button navigation
 *
 * Individual queries that genuinely need real-time data should override
 * staleTime: STALE_TIME.FAST + refetchOnMount: "always" explicitly.
 */
export const PLATFORM_DEFAULT_QUERY_CLIENT_OPTIONS = {
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000, // 5min — cached results stay fresh through normal page navigation
      gcTime: 30 * 60_000, // 30min — keep in memory for back-button + tab switches
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: (failureCount: number, error: unknown) => {
        const status = (error as { status?: number })?.status;
        if (status !== undefined && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
} as const;

export function createPlatformQueryClient(): QueryClient {
  return new QueryClient(PLATFORM_DEFAULT_QUERY_CLIENT_OPTIONS);
}

export const STALE_TIME = {
  FAST: 30_000, // 30s — for tickers / counts that should feel live
  NORMAL: 60_000, // 1min
  SLOW: 5 * 60_000, // 5min — default
  VERY_SLOW: 15 * 60_000, // 15min — for expensive aggregates
  MANUAL: Number.POSITIVE_INFINITY, // never auto-refetch; only on explicit refresh
} as const;
